import { AfterViewInit, ViewChild, Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FuncsService } from '../services/funcs.service';
import { AccountInfoService } from '../services/account.service';
import { Country } from '../data/country';
import { CustomerCurrent } from '../data/customer';
import { ReportService } from '../services/report.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver-es';

export interface Types {
  typeCode: string;
  typeName: string;
}
@Component({
  selector: 'app-potential-billing-report',  
  templateUrl: './potential-billing-report.component.html',
  styleUrls: ['./potential-billing-report.component.css'],
  standalone: false
})
export class PotentialBillingReportComponent implements AfterViewInit {
  dataSource = new MatTableDataSource<any>();

  displayedColumns: string[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  public loading = "Loading...";

  errormsg: string = "";
  msg: string = "";

  dealer: string = "";

  countriesList: Country[] = [];
  selectedCountry = "";

  typesList: Types[] = [];
  selectedType = "";

  tempRole: any = "";
  searchText: string = '';

  constructor(
    private funcs: FuncsService,
    private route: ActivatedRoute,
    private accountInfoService: AccountInfoService,
    private reportService: ReportService
  ) { }

  ngOnInit() {
    this.msg = "";
    this.errormsg = "";
    this.loading = "";

    if (CustomerCurrent.authenticated === "Y" && this.funcs.isAdminRole(CustomerCurrent.role)) {
      this.tempRole = "admin";
    } else {
      this.tempRole = "not";
    }

    this.typesList.push(
      { typeCode: "DEALER", typeName: "DEALER" },
      { typeCode: "FLEET", typeName: "FLEET" }
    );

    this.getCountries(this.tempRole);
    this.selectedCountry = "US";
    this.selectedType = "DEALER";
    this.viewReport();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.dataSource.sortingDataAccessor = (row, columnName) => {
      const value = row[columnName];

      if (value === null || value === undefined) {
        return '';
      }

      if (typeof value === 'number') {
        return value;
      }

      const date = Date.parse(value);
      if (!isNaN(date)) {
        return date;
      }

      return value.toString().toLowerCase();
    };
  }

  getCountries(tempRole: string) {
    this.accountInfoService.getCountries(tempRole).subscribe(countries => {
      countries.forEach(ct => {
        if (ct.countryCode3 == "USA" || ct.countryCode3 == "CAN") {
          this.countriesList.push({ countryName: ct.countryName, countryCode: ct.countryCode, countryCode3: ct.countryCode3, retailRestricted: ct.retailRestricted });
        }
      });
    });
  }

  dateFormat(dateString: string): string {
    return this.funcs.dateFormat(dateString);
  }

  viewReport() {
    this.msg = "";
    this.errormsg = "";
    this.loading = "Loading...";

    if (this.selectedType == "" && this.selectedCountry == "") {
      this.errormsg = "Select Report Type and Country";
      return;
    } else if (this.selectedType != "" && this.selectedCountry == "") {
      this.errormsg = "Select Country";
      return;
    } else if (this.selectedType == "" && this.selectedCountry != "") {
      this.errormsg = "Select Report Type";
      return;
    }

    this.reportService.getPotentialReportData(this.selectedType, this.selectedCountry).subscribe({
      next: (result) => {
        this.displayedColumns = result.columns;
        this.dataSource.data = result.rows;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.dataSource.data.map(row => {
          if (row === "Billing Start Date") {
            this.dateFormat(row);
          } else {
            row;
          }
        })
        this.loading = "";
      },
      error: err => {
        this.errormsg = "Data not available" + err;
        this.loading = "Loading...";
      }
    });
  }

  getFooterValue(col: string) {
    if (col === this.displayedColumns[0]) {
      return 'Total';
    }
    if (col === "Monthly Billing Amount" || col === "Technicians Count" || col === "Subscription Count") {
      const values = this.dataSource.data.map(row => row[col] ? row[col] : 0);

      if (values.every(v => typeof v === 'number')) {
        return values.reduce((a, b) => a + b, 0);
      }
    }
    return '';
  }  

  applyFilter(event: any) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchText = filterValue;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  clearSearch() {
    this.searchText = '';
    this.applyFilter({ target: { value: '' } });
  }


  exportToExcel() {
    var now = new Date();
    var country = this.selectedCountry;
    var reportType = this.selectedType;
    var fileName = `Current Billing Eligibility Report - ${reportType} - ${country} - ${now.toISOString().split('T')[0]}.xlsx`;
    const data = this.dataSource.data.map(row => ({ ...row }));
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);

    // Detect date fields automatically
    const dateColumns: number[] = [];
    const headerColumns = this.displayedColumns;
    const currencyColumns: number[] = [];

    headerColumns.filter((res, index) => {
      if (res === "Billing Date" || res === "Billing Start Date") {
        dateColumns.push(index);
      }
      if (res === "Yearly Cost Per Tech Count" || res === "Yearly Cost Per Subscription" || res === "Monthly Billing Amount") {
        currencyColumns.push(index);
      }
    })

    // Apply date formatting to detected date columns
    const range = XLSX.utils.decode_range(ws['!ref']!);

    dateColumns.forEach(colIndex => {
      for (let row = range.s.r + 1; row <= range.e.r; row++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: colIndex });
        const cell = ws[cellRef];

        if (cell && cell.v) {
          cell.t = 'd';                 // mark as date
          cell.z = 'mm/dd/yyyy';        // Excel date format
          cell.v = new Date(cell.v);    // ensure proper date object
        }
      }
    });

    currencyColumns.forEach(colIndex => {
      for (let row = range.s.r + 1; row <= range.e.r; row++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: colIndex });
        const cell = ws[cellRef];

        if (cell && cell.v != null && cell.v !== '') {
          const numericValue = Number(cell.v);

          if (!isNaN(numericValue)) {
            cell.t = 'n';           // mark as number 
            cell.z = '$#,##0.00';   // Excel currency format 
            cell.v = numericValue;  // ensure numeric value 
          }
        }
      }
    });

    // Create workbook
    const wb: XLSX.WorkBook = {
      Sheets: { Data: ws },
      SheetNames: ['Data']
    };

    // Write file
    const excelBuffer = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
      cellStyles: true
    });

    saveAs(
      new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }),
      fileName
    );
  }
}
